<script setup lang="ts">
import type { Fields } from '@voix/composables/useSlice'
import type {
  ElementInterface,
  SliceInterface,
  SliceSettingsInterface,
} from '@voix/types'

import type { ComputedRef, Ref } from 'vue'
import { useNuxtApp, useState } from '#imports'

import { useElementInView } from '@voix/composables/useElementInView'

import { useHighlightElement } from '@voix/composables/useHighlightElement'
import { useSlice } from '@voix/composables/useSlice'

import { useUtils } from '@voix/composables/useUtils'
import { useAdminStore } from '@voix/store/adminStore'

import { usePageStore } from '@voix/store/pageStore'
import { useSliceStore } from '@voix/store/sliceStore'
import { vElementBounding } from '@vueuse/components'
import { useDebounceFn, useWindowSize } from '@vueuse/core'

import { computed, inject, onMounted, provide, ref, useCssModule, useTemplateRef, watch } from 'vue'
import { findBreakpointSettings } from '../../composables/useBreakpoints'

import {
  buildColorStyles,
  buildFlexStyles,
  buildGridStyles,
  buildMarginStyles,
  buildMaxWidthStyles,
  buildPaddingStyles,
} from '../../composables/useStyle'

defineOptions({ inheritAttrs: false })
const props = defineProps<{
  slice: SliceInterface
}>()
// Import all the slices dynamically
// voixSliceTransform.ts will automatically add the import statements
// like `import { LazySlicesCircleSlideshow } from '#components'` to
// this file at the comment below

// DO NOT REMOVE THESE LINES WHERE WE DEFINE components
let components: Record<string, any> = {}

      import { LazySlicesAdministrationStyleGuide } from '#components'
import { LazySlicesAtlanteanAtlanteanBenefitsIcons } from '#components'
import { LazySlicesAtlanteanGuestServicesInformation } from '#components'
import { LazySlicesAtlanteanPdfViewer } from '#components'
import { LazySlicesAudioAudioPlayer } from '#components'
import { LazySlicesBarbieCard } from '#components'
import { LazySlicesBarbieCircleSlide } from '#components'
import { LazySlicesBarbieCircleSlider } from '#components'
import { LazySlicesBarbieColorCard } from '#components'
import { LazySlicesBarbieContest } from '#components'
import { LazySlicesBarbieContestPhase2 } from '#components'
import { LazySlicesBarbieCopy } from '#components'
import { LazySlicesBarbieCopyFlankingImage } from '#components'
import { LazySlicesBarbieEventCard } from '#components'
import { LazySlicesBarbieSectionIntro } from '#components'
import { LazySlicesBarbieSideCard } from '#components'
import { LazySlicesBarbieSignup } from '#components'
import { LazySlicesBarbieSquareSlide } from '#components'
import { LazySlicesBarbieSquareSlider } from '#components'
import { LazySlicesBarbieSubNavigation } from '#components'
import { LazySlicesBlueprojectAssideArticle } from '#components'
import { LazySlicesBlueprojectBlogIndex } from '#components'
import { LazySlicesBlueprojectColorDisplayCopyBar } from '#components'
import { LazySlicesBlueprojectColorDisplayTitleBar } from '#components'
import { LazySlicesBlueprojectFeatureCard } from '#components'
import { LazySlicesBlueprojectGroupBackground } from '#components'
import { LazySlicesBlueprojectHero } from '#components'
import { LazySlicesBlueprojectImageCard } from '#components'
import { LazySlicesBlueprojectMagazineCard } from '#components'
import { LazySlicesBlueprojectMissionSection } from '#components'
import { LazySlicesBlueprojectTab } from '#components'
import { LazySlicesBlueprojectTabgroup } from '#components'
import { LazySlicesBlueprojectTitleCopy } from '#components'
import { LazySlicesBookingBookNowBar } from '#components'
import { LazySlicesBookingBookNowButton } from '#components'
import { LazySlicesBookingBookNowDirectLink } from '#components'
import { LazySlicesBookingInlineCalendar } from '#components'
import { LazySlicesBrandingLogos } from '#components'
import { LazySlicesCardsCard } from '#components'
import { LazySlicesCardsHorizontalImageCard } from '#components'
import { LazySlicesCardsMosaicSideBySideCards } from '#components'
import { LazySlicesCardsVerticalCardOnTopOfImage } from '#components'
import { LazySlicesCareersCareersTabs } from '#components'
import { LazySlicesCareersHideFiveNineChat } from '#components'
import { LazySlicesCareersTakeoverWrapper } from '#components'
import { LazySlicesClaudiosHero } from '#components'
import { LazySlicesClixEmbed } from '#components'
import { LazySlicesCocodriloDetails } from '#components'
import { LazySlicesCocodriloEvents } from '#components'
import { LazySlicesCocodriloGallery } from '#components'
import { LazySlicesCocodriloHero } from '#components'
import { LazySlicesContainersContainer } from '#components'
import { LazySlicesContainersFullWidthContainer } from '#components'
import { LazySlicesCopyBorderedSquare } from '#components'
import { LazySlicesCopyButtonLink } from '#components'
import { LazySlicesCopyGeneralCopy } from '#components'
import { LazySlicesCopyRestaurantInformation } from '#components'
import { LazySlicesCopyTitleIntroduction } from '#components'
import { LazySlicesCopyTitleWithChevron } from '#components'
import { LazySlicesCopyTwoColumnCard } from '#components'
import { LazySlicesDocumentationSlicePreviewCategory } from '#components'
import { LazySlicesDocumentationSlicePreviews } from '#components'
import { LazySlicesEmailUnsubscribe } from '#components'
import { LazySlicesExperienceFitnessPrograms } from '#components'
import { LazySlicesExperienceShops } from '#components'
import { LazySlicesFamilyPreteensAka } from '#components'
import { LazySlicesFamilyToddlersAka } from '#components'
import { LazySlicesFilelistStacked } from '#components'
import { LazySlicesFormsOpentableReservationWidget } from '#components'
import { LazySlicesFormsReefLogin } from '#components'
import { LazySlicesFormsSpeedRfp } from '#components'
import { LazySlicesGalleriesCategorizedGallery } from '#components'
import { LazySlicesGalleriesCategorizedImage } from '#components'
import { LazySlicesGalleryGalleryContainer } from '#components'
import { LazySlicesGalleryGalleryImage } from '#components'
import { LazySlicesGalleryNavigationGalleryCard } from '#components'
import { LazySlicesGalleryPoweredByFiles } from '#components'
import { LazySlicesGeolocationDemoInfo } from '#components'
import { LazySlicesGeolocationDemoOffer } from '#components'
import { LazySlicesGlobalGenericImage } from '#components'
import { LazySlicesGlueAccordionsAccordionContainer } from '#components'
import { LazySlicesGlueAccordionsAccordionContentThin } from '#components'
import { LazySlicesGlueActivitiesActivityIframePage } from '#components'
import { LazySlicesGlueActivitiesInlineActivitiesCalendar } from '#components'
import { LazySlicesGlueAlertsEyebrow } from '#components'
import { LazySlicesGlueBackgroundsRepeatingBackground } from '#components'
import { LazySlicesGlueBlogBlogIndex } from '#components'
import { LazySlicesGlueBookingGlueInlineCalendar } from '#components'
import { LazySlicesGlueBookingIntroAndBook } from '#components'
import { LazySlicesGlueButtonsGenericButton } from '#components'
import { LazySlicesGlueContainersGridContainer } from '#components'
import { LazySlicesGlueContainersOneThirdImageCardGrid } from '#components'
import { LazySlicesGlueContainersWhiteCard } from '#components'
import { LazySlicesGlueDiningDiningFeature } from '#components'
import { LazySlicesGlueDiningDiningFilter } from '#components'
import { LazySlicesGlueDiningDiningRestaurantMasthead } from '#components'
import { LazySlicesGlueDiningDiningResults } from '#components'
import { LazySlicesGlueDividersDivider } from '#components'
import { LazySlicesGlueDividersDividerFull } from '#components'
import { LazySlicesGlueEventsAllEvents } from '#components'
import { LazySlicesGlueEventsAllEventsIndex } from '#components'
import { LazySlicesGlueEventsDailyEvents } from '#components'
import { LazySlicesGlueEventsEvent } from '#components'
import { LazySlicesGlueEventsFeatureCard } from '#components'
import { LazySlicesGlueEventsVenueFeaturedEvents } from '#components'
import { LazySlicesGlueExperiencesExperienceCard } from '#components'
import { LazySlicesGlueExperiencesFilterableWrapper } from '#components'
import { LazySlicesGlueExperiencesMarineAdventuresSearch } from '#components'
import { LazySlicesGlueExperiencesThreeTabFilter } from '#components'
import { LazySlicesGlueGalleriesCategorizedGallery } from '#components'
import { LazySlicesGlueGalleriesCategorizedGalleryImage } from '#components'
import { LazySlicesGlueGalleriesNavigationGallery } from '#components'
import { LazySlicesGlueGalleriesNavigationGalleryLink } from '#components'
import { LazySlicesGlueGalleriesOlapic } from '#components'
import { LazySlicesGlueGalleriesStaticGriddedGallery } from '#components'
import { LazySlicesGlueGalleriesStaticGriddedGalleryImage } from '#components'
import { LazySlicesGlueGalleriesStaticGriddedGallerySlotFive } from '#components'
import { LazySlicesGlueGalleriesStaticGriddedGallerySlotFour } from '#components'
import { LazySlicesGlueGalleriesStaticGriddedGallerySlotOne } from '#components'
import { LazySlicesGlueGalleriesStaticGriddedGallerySlotThree } from '#components'
import { LazySlicesGlueGalleriesStaticGriddedGallerySlotTwo } from '#components'
import { LazySlicesGlueGalleriesThreeImageGridToMobileSlider } from '#components'
import { LazySlicesGlueGenericCardsBookingCard } from '#components'
import { LazySlicesGlueGenericCardsCardWithGrid } from '#components'
import { LazySlicesGlueGenericCardsClippedCard } from '#components'
import { LazySlicesGlueGenericCardsContactCard } from '#components'
import { LazySlicesGlueGenericCardsDescriptionCard } from '#components'
import { LazySlicesGlueGenericCardsFlatCard } from '#components'
import { LazySlicesGlueGenericCardsGridOfCardsRedTitleCard } from '#components'
import { LazySlicesGlueGenericCardsHorizontalImageCard } from '#components'
import { LazySlicesGlueGenericCardsIconCard } from '#components'
import { LazySlicesGlueGenericCardsImageCard } from '#components'
import { LazySlicesGlueGenericCardsOneThirdImageCard } from '#components'
import { LazySlicesGlueGenericCardsOneToThreeCardsWithRedCircleIcon } from '#components'
import { LazySlicesGlueGenericCardsThinImage } from '#components'
import { LazySlicesGlueGenericCardsWideMediaCard } from '#components'
import { LazySlicesGlueHistoryHistoryGroup } from '#components'
import { LazySlicesGlueHistoryHistoryItem } from '#components'
import { LazySlicesGlueHotelFeaturesDailyResortFee } from '#components'
import { LazySlicesGlueHotelsHotelIndex } from '#components'
import { LazySlicesGlueHotelsOtherHotelCard } from '#components'
import { LazySlicesGlueHotelsOtherHotels } from '#components'
import { LazySlicesGlueImagesImageFourByThree } from '#components'
import { LazySlicesGlueImagesImageLink } from '#components'
import { LazySlicesGlueImagesImageSquare } from '#components'
import { LazySlicesGlueMagazineFeatureMagazineFeature } from '#components'
import { LazySlicesGlueMagazineSliderMagazineSlide } from '#components'
import { LazySlicesGlueMagazineSliderMagazineSliderContainer } from '#components'
import { LazySlicesGlueMapFlightMap } from '#components'
import { LazySlicesGlueMapInteractiveMap } from '#components'
import { LazySlicesGlueMediaClippedMedia } from '#components'
import { LazySlicesGlueMobileAppMobileAppHero } from '#components'
import { LazySlicesGlueMoviesMovies } from '#components'
import { LazySlicesGlueNavigationSubNavigation } from '#components'
import { LazySlicesGluePressReleasesMagazineCoverImage } from '#components'
import { LazySlicesGluePressReleasesPressReleaseIndex } from '#components'
import { LazySlicesGlueScavengerHuntWinningForm } from '#components'
import { LazySlicesGlueSlidersBottomRightCopy } from '#components'
import { LazySlicesGlueSlidersCircleSlide } from '#components'
import { LazySlicesGlueSlidersCircleSlider } from '#components'
import { LazySlicesGlueSlidersClippedSlider } from '#components'
import { LazySlicesGlueSlidersClippedSliderFullImageCard } from '#components'
import { LazySlicesGlueSlidersSideClippedSlider } from '#components'
import { LazySlicesGlueSlidersSliderContainer } from '#components'
import { LazySlicesGlueSlidersSliderImage } from '#components'
import { LazySlicesGlueSlidersTitleDescriptionFlankingGallery } from '#components'
import { LazySlicesGlueSpecialsDeals } from '#components'
import { LazySlicesGlueSpecialsSpecialPage } from '#components'
import { LazySlicesGlueSpecialsSpecials } from '#components'
import { LazySlicesGlueSpecialsSpecialsSlideOut } from '#components'
import { LazySlicesGlueStayBedType } from '#components'
import { LazySlicesGlueStayBedTypeList } from '#components'
import { LazySlicesGlueStayFeaturedBedType } from '#components'
import { LazySlicesGlueStayFeaturedBedTypes } from '#components'
import { LazySlicesGlueStayGridGallery } from '#components'
import { LazySlicesGlueStayHotel } from '#components'
import { LazySlicesGlueStayHotelHero } from '#components'
import { LazySlicesGlueStayHotelWrapper } from '#components'
import { LazySlicesGlueStayRoomType } from '#components'
import { LazySlicesGlueStayRoomTypeList } from '#components'
import { LazySlicesGlueTabsystemTabSystemContainer } from '#components'
import { LazySlicesGlueTabsystemTabSystemTab } from '#components'
import { LazySlicesGlueTitlesAndTextAgencyTitleAndCopy } from '#components'
import { LazySlicesGlueTitlesAndTextGeneralTitleAndCopy } from '#components'
import { LazySlicesGlueTitlesAndTextTitleAndMedia } from '#components'
import { LazySlicesGlueVerticalScrollerVerticalScrollContainer } from '#components'
import { LazySlicesGlueVerticalScrollerVerticalScrollItem } from '#components'
import { LazySlicesGlueWaitingListWaitingList } from '#components'
import { LazySlicesGolfScoreCard } from '#components'
import { LazySlicesHeroesBisectedHero } from '#components'
import { LazySlicesHeroesLargeImageBackground } from '#components'
import { LazySlicesHeroesMainHero } from '#components'
import { LazySlicesHolidayCardHolidayCard } from '#components'
import { LazySlicesIframeLarge } from '#components'
import { LazySlicesKosawanAbout } from '#components'
import { LazySlicesKosawanHeader } from '#components'
import { LazySlicesKosawanHero } from '#components'
import { LazySlicesMarigoldAtlantisGrowPopup } from '#components'
import { LazySlicesMenusMenu } from '#components'
import { LazySlicesMusicMakingWavesMmwEventCard } from '#components'
import { LazySlicesMusicMakingWavesMmwExcerptIntroduction } from '#components'
import { LazySlicesMusicMakingWavesMmwFeatures } from '#components'
import { LazySlicesMusicMakingWavesMmwHeadline } from '#components'
import { LazySlicesMusicMakingWavesMmwMagazine } from '#components'
import { LazySlicesMusicMakingWavesMmwSliderHero } from '#components'
import { LazySlicesMusicMakingWavesMmwThreeImageDivider } from '#components'
import { LazySlicesMusicMakingWavesMmwTitleIntroduction } from '#components'
import { LazySlicesMusicMakingWavesMmwVenueCards } from '#components'
import { LazySlicesMusicMakingWavesMmwWavesDivider } from '#components'
import { LazySlicesNavigationSubNavigationBar } from '#components'
import { LazySlicesNavigationSubnavigation } from '#components'
import { LazySlicesOnetrustPrivacyPolicy } from '#components'
import { LazySlicesOverridesHideNavigationBookNow } from '#components'
import { LazySlicesParanzaCardFlankingImage } from '#components'
import { LazySlicesParanzaHeroImage } from '#components'
import { LazySlicesParanzaLinkWithArrow } from '#components'
import { LazySlicesParanzaMagazineImage } from '#components'
import { LazySlicesParanzaRestaurantIntro } from '#components'
import { LazySlicesParanzaSinglePlatform } from '#components'
import { LazySlicesParanzaStaggardCards } from '#components'
import { LazySlicesParanzaTextFlankingImage } from '#components'
import { LazySlicesParanzaTitleHero } from '#components'
import { LazySlicesReefMyAccount } from '#components'
import { LazySlicesReefProperties } from '#components'
import { LazySlicesReefWrapper } from '#components'
import { LazySlicesSapphireBlockquoteOnBackground } from '#components'
import { LazySlicesSapphireInformationFlankingGallery } from '#components'
import { LazySlicesSapphireOverlappingCardOnImage } from '#components'
import { LazySlicesSapphireSapphireFooter } from '#components'
import { LazySlicesSlidersGeneralSlider } from '#components'
import { LazySlicesSlidersSliderContainer } from '#components'
import { LazySlicesSlidersSliderImage } from '#components'
import { LazySlicesSlidersSliderImageShort } from '#components'
import { LazySlicesSlidersSliderVimeoVideo } from '#components'
import { LazySlicesSocialShareOnFacebook } from '#components'
import { LazySlicesSpecialOffersCountdown } from '#components'
import { LazySlicesSpecialOffersDiscover } from '#components'
import { LazySlicesSpecialOffersGeotargeted } from '#components'
import { LazySlicesSpecialOffersHighlights } from '#components'
import { LazySlicesSpecialOffersSavings } from '#components'
import { LazySlicesSpecialOffersTerms } from '#components'
import { LazySlicesSummerTwentyTwentyFourImageOverDuelingInfo } from '#components'
import { LazySlicesSummerTwentyTwentyFourImageOverFeatures } from '#components'
import { LazySlicesSummerTwentyTwentyTwoOlapic } from '#components'
import { LazySlicesTravelAgentsLogin } from '#components'
import { LazySlicesTravelAgentsRegistration } from '#components'
import { LazySlicesTwentyFifthAnniversaryAtlantisAnnvLogo } from '#components'
import { LazySlicesTwentyFifthAnniversaryIntroduction } from '#components'
import { LazySlicesTwentyFifthAnniversaryLargeTextOverImage } from '#components'
import { LazySlicesTwentyFifthAnniversaryMemoryGame } from '#components'
import { LazySlicesTwentyFifthAnniversarySectionHeader } from '#components'
import { LazySlicesTwentyFifthAnniversarySquareGallery } from '#components'
import { LazySlicesTwentyFifthAnniversaryTombstoneSlide } from '#components'
import { LazySlicesVideoWildKingdom } from '#components'
import { LazySlicesVideoWistiaChannel } from '#components'
import { LazySlicesWffEventBubbles } from '#components'
import { LazySlicesWffEventChefSlider } from '#components'
import { LazySlicesWffEventChefSliderCard } from '#components'
import { LazySlicesWffEventDetails } from '#components'
import { LazySlicesWffEventGallery } from '#components'
import { LazySlicesWffEventHero } from '#components'
import { LazySlicesWffEventInfo } from '#components'
import { LazySlicesWffEventMoreInfoCard } from '#components'
import { LazySlicesWffEventSlider } from '#components'
import { LazySlicesWffEventSliderImage } from '#components'
import { LazySlicesWffEventSponsorLogo } from '#components'
import { LazySlicesWffEventSponsors } from '#components'
import { LazySlicesWffEvents } from '#components'
import { LazySlicesWffGeneralTitleAndCopy } from '#components'
import { LazySlicesWffImageCard } from '#components'
import { LazySlicesWffImageWithColorCopyBox } from '#components'
import { LazySlicesWffIntroduction } from '#components'
import { LazySlicesWffLogo } from '#components'
import { LazySlicesWffLogoGroup } from '#components'
import { LazySlicesWffSlider } from '#components'
import { LazySlicesWffVideoWithDescription } from '#components'

     components = {
      LazySlicesAdministrationStyleGuide,
  LazySlicesAtlanteanAtlanteanBenefitsIcons,
  LazySlicesAtlanteanGuestServicesInformation,
  LazySlicesAtlanteanPdfViewer,
  LazySlicesAudioAudioPlayer,
  LazySlicesBarbieCard,
  LazySlicesBarbieCircleSlide,
  LazySlicesBarbieCircleSlider,
  LazySlicesBarbieColorCard,
  LazySlicesBarbieContest,
  LazySlicesBarbieContestPhase2,
  LazySlicesBarbieCopy,
  LazySlicesBarbieCopyFlankingImage,
  LazySlicesBarbieEventCard,
  LazySlicesBarbieSectionIntro,
  LazySlicesBarbieSideCard,
  LazySlicesBarbieSignup,
  LazySlicesBarbieSquareSlide,
  LazySlicesBarbieSquareSlider,
  LazySlicesBarbieSubNavigation,
  LazySlicesBlueprojectAssideArticle,
  LazySlicesBlueprojectBlogIndex,
  LazySlicesBlueprojectColorDisplayCopyBar,
  LazySlicesBlueprojectColorDisplayTitleBar,
  LazySlicesBlueprojectFeatureCard,
  LazySlicesBlueprojectGroupBackground,
  LazySlicesBlueprojectHero,
  LazySlicesBlueprojectImageCard,
  LazySlicesBlueprojectMagazineCard,
  LazySlicesBlueprojectMissionSection,
  LazySlicesBlueprojectTab,
  LazySlicesBlueprojectTabgroup,
  LazySlicesBlueprojectTitleCopy,
  LazySlicesBookingBookNowBar,
  LazySlicesBookingBookNowButton,
  LazySlicesBookingBookNowDirectLink,
  LazySlicesBookingInlineCalendar,
  LazySlicesBrandingLogos,
  LazySlicesCardsCard,
  LazySlicesCardsHorizontalImageCard,
  LazySlicesCardsMosaicSideBySideCards,
  LazySlicesCardsVerticalCardOnTopOfImage,
  LazySlicesCareersCareersTabs,
  LazySlicesCareersHideFiveNineChat,
  LazySlicesCareersTakeoverWrapper,
  LazySlicesClaudiosHero,
  LazySlicesClixEmbed,
  LazySlicesCocodriloDetails,
  LazySlicesCocodriloEvents,
  LazySlicesCocodriloGallery,
  LazySlicesCocodriloHero,
  LazySlicesContainersContainer,
  LazySlicesContainersFullWidthContainer,
  LazySlicesCopyBorderedSquare,
  LazySlicesCopyButtonLink,
  LazySlicesCopyGeneralCopy,
  LazySlicesCopyRestaurantInformation,
  LazySlicesCopyTitleIntroduction,
  LazySlicesCopyTitleWithChevron,
  LazySlicesCopyTwoColumnCard,
  LazySlicesDocumentationSlicePreviewCategory,
  LazySlicesDocumentationSlicePreviews,
  LazySlicesEmailUnsubscribe,
  LazySlicesExperienceFitnessPrograms,
  LazySlicesExperienceShops,
  LazySlicesFamilyPreteensAka,
  LazySlicesFamilyToddlersAka,
  LazySlicesFilelistStacked,
  LazySlicesFormsOpentableReservationWidget,
  LazySlicesFormsReefLogin,
  LazySlicesFormsSpeedRfp,
  LazySlicesGalleriesCategorizedGallery,
  LazySlicesGalleriesCategorizedImage,
  LazySlicesGalleryGalleryContainer,
  LazySlicesGalleryGalleryImage,
  LazySlicesGalleryNavigationGalleryCard,
  LazySlicesGalleryPoweredByFiles,
  LazySlicesGeolocationDemoInfo,
  LazySlicesGeolocationDemoOffer,
  LazySlicesGlobalGenericImage,
  LazySlicesGlueAccordionsAccordionContainer,
  LazySlicesGlueAccordionsAccordionContentThin,
  LazySlicesGlueActivitiesActivityIframePage,
  LazySlicesGlueActivitiesInlineActivitiesCalendar,
  LazySlicesGlueAlertsEyebrow,
  LazySlicesGlueBackgroundsRepeatingBackground,
  LazySlicesGlueBlogBlogIndex,
  LazySlicesGlueBookingGlueInlineCalendar,
  LazySlicesGlueBookingIntroAndBook,
  LazySlicesGlueButtonsGenericButton,
  LazySlicesGlueContainersGridContainer,
  LazySlicesGlueContainersOneThirdImageCardGrid,
  LazySlicesGlueContainersWhiteCard,
  LazySlicesGlueDiningDiningFeature,
  LazySlicesGlueDiningDiningFilter,
  LazySlicesGlueDiningDiningRestaurantMasthead,
  LazySlicesGlueDiningDiningResults,
  LazySlicesGlueDividersDivider,
  LazySlicesGlueDividersDividerFull,
  LazySlicesGlueEventsAllEvents,
  LazySlicesGlueEventsAllEventsIndex,
  LazySlicesGlueEventsDailyEvents,
  LazySlicesGlueEventsEvent,
  LazySlicesGlueEventsFeatureCard,
  LazySlicesGlueEventsVenueFeaturedEvents,
  LazySlicesGlueExperiencesExperienceCard,
  LazySlicesGlueExperiencesFilterableWrapper,
  LazySlicesGlueExperiencesMarineAdventuresSearch,
  LazySlicesGlueExperiencesThreeTabFilter,
  LazySlicesGlueGalleriesCategorizedGallery,
  LazySlicesGlueGalleriesCategorizedGalleryImage,
  LazySlicesGlueGalleriesNavigationGallery,
  LazySlicesGlueGalleriesNavigationGalleryLink,
  LazySlicesGlueGalleriesOlapic,
  LazySlicesGlueGalleriesStaticGriddedGallery,
  LazySlicesGlueGalleriesStaticGriddedGalleryImage,
  LazySlicesGlueGalleriesStaticGriddedGallerySlotFive,
  LazySlicesGlueGalleriesStaticGriddedGallerySlotFour,
  LazySlicesGlueGalleriesStaticGriddedGallerySlotOne,
  LazySlicesGlueGalleriesStaticGriddedGallerySlotThree,
  LazySlicesGlueGalleriesStaticGriddedGallerySlotTwo,
  LazySlicesGlueGalleriesThreeImageGridToMobileSlider,
  LazySlicesGlueGenericCardsBookingCard,
  LazySlicesGlueGenericCardsCardWithGrid,
  LazySlicesGlueGenericCardsClippedCard,
  LazySlicesGlueGenericCardsContactCard,
  LazySlicesGlueGenericCardsDescriptionCard,
  LazySlicesGlueGenericCardsFlatCard,
  LazySlicesGlueGenericCardsGridOfCardsRedTitleCard,
  LazySlicesGlueGenericCardsHorizontalImageCard,
  LazySlicesGlueGenericCardsIconCard,
  LazySlicesGlueGenericCardsImageCard,
  LazySlicesGlueGenericCardsOneThirdImageCard,
  LazySlicesGlueGenericCardsOneToThreeCardsWithRedCircleIcon,
  LazySlicesGlueGenericCardsThinImage,
  LazySlicesGlueGenericCardsWideMediaCard,
  LazySlicesGlueHistoryHistoryGroup,
  LazySlicesGlueHistoryHistoryItem,
  LazySlicesGlueHotelFeaturesDailyResortFee,
  LazySlicesGlueHotelsHotelIndex,
  LazySlicesGlueHotelsOtherHotelCard,
  LazySlicesGlueHotelsOtherHotels,
  LazySlicesGlueImagesImageFourByThree,
  LazySlicesGlueImagesImageLink,
  LazySlicesGlueImagesImageSquare,
  LazySlicesGlueMagazineFeatureMagazineFeature,
  LazySlicesGlueMagazineSliderMagazineSlide,
  LazySlicesGlueMagazineSliderMagazineSliderContainer,
  LazySlicesGlueMapFlightMap,
  LazySlicesGlueMapInteractiveMap,
  LazySlicesGlueMediaClippedMedia,
  LazySlicesGlueMobileAppMobileAppHero,
  LazySlicesGlueMoviesMovies,
  LazySlicesGlueNavigationSubNavigation,
  LazySlicesGluePressReleasesMagazineCoverImage,
  LazySlicesGluePressReleasesPressReleaseIndex,
  LazySlicesGlueScavengerHuntWinningForm,
  LazySlicesGlueSlidersBottomRightCopy,
  LazySlicesGlueSlidersCircleSlide,
  LazySlicesGlueSlidersCircleSlider,
  LazySlicesGlueSlidersClippedSlider,
  LazySlicesGlueSlidersClippedSliderFullImageCard,
  LazySlicesGlueSlidersSideClippedSlider,
  LazySlicesGlueSlidersSliderContainer,
  LazySlicesGlueSlidersSliderImage,
  LazySlicesGlueSlidersTitleDescriptionFlankingGallery,
  LazySlicesGlueSpecialsDeals,
  LazySlicesGlueSpecialsSpecialPage,
  LazySlicesGlueSpecialsSpecials,
  LazySlicesGlueSpecialsSpecialsSlideOut,
  LazySlicesGlueStayBedType,
  LazySlicesGlueStayBedTypeList,
  LazySlicesGlueStayFeaturedBedType,
  LazySlicesGlueStayFeaturedBedTypes,
  LazySlicesGlueStayGridGallery,
  LazySlicesGlueStayHotel,
  LazySlicesGlueStayHotelHero,
  LazySlicesGlueStayHotelWrapper,
  LazySlicesGlueStayRoomType,
  LazySlicesGlueStayRoomTypeList,
  LazySlicesGlueTabsystemTabSystemContainer,
  LazySlicesGlueTabsystemTabSystemTab,
  LazySlicesGlueTitlesAndTextAgencyTitleAndCopy,
  LazySlicesGlueTitlesAndTextGeneralTitleAndCopy,
  LazySlicesGlueTitlesAndTextTitleAndMedia,
  LazySlicesGlueVerticalScrollerVerticalScrollContainer,
  LazySlicesGlueVerticalScrollerVerticalScrollItem,
  LazySlicesGlueWaitingListWaitingList,
  LazySlicesGolfScoreCard,
  LazySlicesHeroesBisectedHero,
  LazySlicesHeroesLargeImageBackground,
  LazySlicesHeroesMainHero,
  LazySlicesHolidayCardHolidayCard,
  LazySlicesIframeLarge,
  LazySlicesKosawanAbout,
  LazySlicesKosawanHeader,
  LazySlicesKosawanHero,
  LazySlicesMarigoldAtlantisGrowPopup,
  LazySlicesMenusMenu,
  LazySlicesMusicMakingWavesMmwEventCard,
  LazySlicesMusicMakingWavesMmwExcerptIntroduction,
  LazySlicesMusicMakingWavesMmwFeatures,
  LazySlicesMusicMakingWavesMmwHeadline,
  LazySlicesMusicMakingWavesMmwMagazine,
  LazySlicesMusicMakingWavesMmwSliderHero,
  LazySlicesMusicMakingWavesMmwThreeImageDivider,
  LazySlicesMusicMakingWavesMmwTitleIntroduction,
  LazySlicesMusicMakingWavesMmwVenueCards,
  LazySlicesMusicMakingWavesMmwWavesDivider,
  LazySlicesNavigationSubNavigationBar,
  LazySlicesNavigationSubnavigation,
  LazySlicesOnetrustPrivacyPolicy,
  LazySlicesOverridesHideNavigationBookNow,
  LazySlicesParanzaCardFlankingImage,
  LazySlicesParanzaHeroImage,
  LazySlicesParanzaLinkWithArrow,
  LazySlicesParanzaMagazineImage,
  LazySlicesParanzaRestaurantIntro,
  LazySlicesParanzaSinglePlatform,
  LazySlicesParanzaStaggardCards,
  LazySlicesParanzaTextFlankingImage,
  LazySlicesParanzaTitleHero,
  LazySlicesReefMyAccount,
  LazySlicesReefProperties,
  LazySlicesReefWrapper,
  LazySlicesSapphireBlockquoteOnBackground,
  LazySlicesSapphireInformationFlankingGallery,
  LazySlicesSapphireOverlappingCardOnImage,
  LazySlicesSapphireSapphireFooter,
  LazySlicesSlidersGeneralSlider,
  LazySlicesSlidersSliderContainer,
  LazySlicesSlidersSliderImage,
  LazySlicesSlidersSliderImageShort,
  LazySlicesSlidersSliderVimeoVideo,
  LazySlicesSocialShareOnFacebook,
  LazySlicesSpecialOffersCountdown,
  LazySlicesSpecialOffersDiscover,
  LazySlicesSpecialOffersGeotargeted,
  LazySlicesSpecialOffersHighlights,
  LazySlicesSpecialOffersSavings,
  LazySlicesSpecialOffersTerms,
  LazySlicesSummerTwentyTwentyFourImageOverDuelingInfo,
  LazySlicesSummerTwentyTwentyFourImageOverFeatures,
  LazySlicesSummerTwentyTwentyTwoOlapic,
  LazySlicesTravelAgentsLogin,
  LazySlicesTravelAgentsRegistration,
  LazySlicesTwentyFifthAnniversaryAtlantisAnnvLogo,
  LazySlicesTwentyFifthAnniversaryIntroduction,
  LazySlicesTwentyFifthAnniversaryLargeTextOverImage,
  LazySlicesTwentyFifthAnniversaryMemoryGame,
  LazySlicesTwentyFifthAnniversarySectionHeader,
  LazySlicesTwentyFifthAnniversarySquareGallery,
  LazySlicesTwentyFifthAnniversaryTombstoneSlide,
  LazySlicesVideoWildKingdom,
  LazySlicesVideoWistiaChannel,
  LazySlicesWffEventBubbles,
  LazySlicesWffEventChefSlider,
  LazySlicesWffEventChefSliderCard,
  LazySlicesWffEventDetails,
  LazySlicesWffEventGallery,
  LazySlicesWffEventHero,
  LazySlicesWffEventInfo,
  LazySlicesWffEventMoreInfoCard,
  LazySlicesWffEventSlider,
  LazySlicesWffEventSliderImage,
  LazySlicesWffEventSponsorLogo,
  LazySlicesWffEventSponsors,
  LazySlicesWffEvents,
  LazySlicesWffGeneralTitleAndCopy,
  LazySlicesWffImageCard,
  LazySlicesWffImageWithColorCopyBox,
  LazySlicesWffIntroduction,
  LazySlicesWffLogo,
  LazySlicesWffLogoGroup,
  LazySlicesWffSlider,
  LazySlicesWffVideoWithDescription
     }
      

const resolvedComponent = components[`Lazy${props.slice.component}`]

const { isTopLevelWindow } = useUtils()
const sliceId = computed(() => {
  return `voix-slice-${props.slice.id}`
})

const adminStore = useAdminStore()
const sliceStore = useSliceStore()
const sliceHelper = useSlice()
const sliceConfig = sliceStore.getSlice(props.slice.component)
const pageStore = usePageStore()

const fieldsProxy: Ref<Fields | null> = ref(null)

// Field Data
if (sliceConfig && props.slice.fields)
  fieldsProxy.value = sliceHelper.resolveFields(sliceConfig, props.slice.fields)

// We need to watch the fields for edits so that we can update the
// fieldsProxy value. This is necessary because we need to be able to
// be reactive to changes in the fields.
watch(
  () => props.slice,
  () => {
    if (sliceConfig && props.slice.fields)
      fieldsProxy.value = sliceHelper.resolveFields(sliceConfig, props.slice.fields)
  },
  { deep: true },
)

const { $voix } = useNuxtApp()
const sliceRef = useTemplateRef('sliceRef')
provide('sliceRef', sliceRef)

const fullTailwindConfig = $voix.options.tailwind.config

// Are there styles across any of the breakpoints
const hasStyles = computed(() => {
  const hasStylesObj = {
    paddingTop: false,
    paddingRight: false,
    paddingBottom: false,
    paddingLeft: false,
    marginTop: false,
    marginRight: false,
    marginBottom: false,
    marginLeft: false,
    color: false,
    backgroundColor: false,
  }

  // Loop through all the breakpoints and see if there is a style set
  if (props.slice.properties?.settings) {
    const settings = props.slice.properties.settings
    settings.forEach((breakpoint) => {
      if (breakpoint.padding?.top)
        hasStylesObj.paddingTop = true

      if (breakpoint.padding?.right)
        hasStylesObj.paddingRight = true

      if (breakpoint.padding?.bottom)
        hasStylesObj.paddingBottom = true

      if (breakpoint.padding?.left)
        hasStylesObj.paddingLeft = true

      if (breakpoint.margin?.top)
        hasStylesObj.marginTop = true

      if (breakpoint.margin?.right)
        hasStylesObj.marginRight = true

      if (breakpoint.margin?.bottom)
        hasStylesObj.marginBottom = true

      if (breakpoint.margin?.left)
        hasStylesObj.marginLeft = true

      if (breakpoint.color)
        hasStylesObj.color = true

      if (breakpoint.backgroundColor)
        hasStylesObj.backgroundColor = true
    })
  }

  return hasStylesObj
})

function containsClass(classArray: Array<string>, className: string) {
  const classes = classArray.find((item) => {
    if (typeof item === 'string') {
    // Use startsWith to check if the string starts with the className
      return item.startsWith(className)
    }
    return false
  })
  if (classes) {
    // remove the className from the string
    const descriptorValue = classes.replace(className, '')
    if (descriptorValue) {
      if (!Number.isNaN(Number(descriptorValue))) {
        return fullTailwindConfig.theme.spacing[descriptorValue]
      }
      else {
        // Split the colors by dash and crawl the fullTailwindConfig.theme.colors object
        return descriptorValue.split('-').reduce((acc, key) => {
          if (acc[key]) {
            return acc[key]
          }
          return acc
        }, fullTailwindConfig.theme.colors)
      }
    }
  }
  return null
}

const finalSliceClasses: ComputedRef<Array<string>> = computed(() => {
  const finalClasses = []
  if (hasStyles.value.paddingTop)
    finalClasses.push('voix-slice-padding-top')
  if (hasStyles.value.paddingRight)
    finalClasses.push('voix-slice-padding-right')
  if (hasStyles.value.paddingBottom)
    finalClasses.push('voix-slice-padding-bottom')
  if (hasStyles.value.paddingLeft)
    finalClasses.push('voix-slice-padding-left')
  if (hasStyles.value.marginTop)
    finalClasses.push('voix-slice-margin-top')
  if (hasStyles.value.marginRight)
    finalClasses.push('voix-slice-margin-right')
  if (hasStyles.value.marginBottom)
    finalClasses.push('voix-slice-margin-bottom')
  if (hasStyles.value.marginLeft)
    finalClasses.push('voix-slice-margin-left')
  if (hasStyles.value.color)
    finalClasses.push('voix-slice-color')
  if (hasStyles.value.backgroundColor)
    finalClasses.push('voix-slice-background-color')

  return finalClasses
})

// Slice Styles
const sliceStyles: Ref<{
  [key: string]: string | number
}> = ref({})

function generateSliceStyles() {
  const theSlice = Object.assign({}, props.slice)

  if (!props.slice)
    return true

  let styles: Record<string, string | number> = {}

  if (theSlice.properties && theSlice.properties.settings) {
    const breakpointSettings = findBreakpointSettings(
      theSlice.properties.settings,
    ) as SliceSettingsInterface | null

    if (breakpointSettings) {
      if (breakpointSettings.position)
        styles.position = breakpointSettings.position

      styles = {
        ...styles,
        ...buildFlexStyles(styles, breakpointSettings),
        ...buildGridStyles(styles, breakpointSettings),
        ...buildMaxWidthStyles(styles, breakpointSettings),
        ...buildMarginStyles(styles, breakpointSettings),
        ...buildPaddingStyles(styles, breakpointSettings),
        ...buildColorStyles(styles, breakpointSettings),
      }
    }
  }
  Object.keys(hasStyles.value).forEach((key) => {
    if ((styles[key] === null || styles[key] === undefined) && hasStyles.value[key]) {
      // Ok, there is a style for this property on some breakpoint but not at this breakpoint. Let's apply whatever
      // we can find from the classes on the slice root.
      if (sliceRef.value?.$el) {
        const classList = Array.from(sliceRef.value.$el.classList) as Array<string>

        // calculate if any of the classList Items include part of a string 'p-'
        if (key === 'paddingTop' && containsClass(classList, 'pt-'))
          styles.paddingTop = containsClass(classList, 'pt-')
        else if (key === 'paddingTop' && containsClass(classList, 'py-'))
          styles.paddingTop = containsClass(classList, 'py-')
        else if (key === 'paddingTop' && containsClass(classList, 'p-'))
          styles.paddingTop = containsClass(classList, 'p-')

        if (key === 'paddingRight' && containsClass(classList, 'pr-'))
          styles.paddingRight = containsClass(classList, 'pr-')
        else if (key === 'paddingRight' && containsClass(classList, 'px-'))
          styles.paddingRight = containsClass(classList, 'px-')
        else if (key === 'paddingRight' && containsClass(classList, 'p-'))
          styles.paddingRight = containsClass(classList, 'p-')

        if (key === 'paddingBottom' && containsClass(classList, 'pb-'))
          styles.paddingBottom = containsClass(classList, 'pb-')
        else if (key === 'paddingBottom' && containsClass(classList, 'py-'))
          styles.paddingBottom = containsClass(classList, 'py-')
        else if (key === 'paddingBottom' && containsClass(classList, 'p-'))
          styles.paddingBottom = containsClass(classList, 'p-')

        if (key === 'paddingLeft' && containsClass(classList, 'pl-'))
          styles.paddingLeft = containsClass(classList, 'pl-')
        else if (key === 'paddingLeft' && containsClass(classList, 'px-'))
          styles.paddingLeft = containsClass(classList, 'px-')
        else if (key === 'paddingLeft' && containsClass(classList, 'p-'))
          styles.paddingLeft = containsClass(classList, 'p-')

        if (key === 'marginTop' && containsClass(classList, 'mt-'))
          styles.marginTop = containsClass(classList, 'mt-')
        else if (key === 'marginTop' && containsClass(classList, 'my-'))
          styles.marginTop = containsClass(classList, 'my-')
        else if (key === 'marginTop' && containsClass(classList, 'm-'))
          styles.marginTop = containsClass(classList, 'm-')

        if (key === 'marginRight' && containsClass(classList, 'mr-'))
          styles.marginRight = containsClass(classList, 'mr-')
        else if (key === 'marginRight' && containsClass(classList, 'mx-'))
          styles.marginRight = containsClass(classList, 'mx-')
        else if (key === 'marginRight' && containsClass(classList, 'm-'))
          styles.marginRight = containsClass(classList, 'm-')

        if (key === 'marginBottom' && containsClass(classList, 'mb-'))
          styles.marginBottom = containsClass(classList, 'mb-')
        else if (key === 'marginBottom' && containsClass(classList, 'my-'))
          styles.marginBottom = containsClass(classList, 'my-')
        else if (key === 'marginBottom' && containsClass(classList, 'm-'))
          styles.marginBottom = containsClass(classList, 'm-')

        if (key === 'marginLeft' && containsClass(classList, 'ml-'))
          styles.marginLeft = containsClass(classList, 'ml-')
        else if (key === 'marginLeft' && containsClass(classList, 'mx-'))
          styles.marginLeft = containsClass(classList, 'mx-')
        else if (key === 'marginLeft' && containsClass(classList, 'm-'))
          styles.marginLeft = containsClass(classList, 'm-')

        if (key === 'color' && containsClass(classList, 'text-'))
          styles.color = containsClass(classList, 'text-')

        if (key === 'backgroundColor' && containsClass(classList, 'bg-'))
          styles.backgroundColor = containsClass(classList, 'bg-')
      }
    }
  })

  sliceStyles.value = Object.assign({}, styles)

  return true
}
// Initial generation of styles
generateSliceStyles()
const updateSliceStyles = useDebounceFn(
  generateSliceStyles,
  10,
  { maxWait: 50 },
)

onMounted(() => {
  generateSliceStyles()
})

// Reset styles on window resize
const { width: windowWidth, height: windowHeight } = useWindowSize()
watch([windowWidth, windowHeight], () => {
  updateSliceStyles()
})

const settings = computed(() => {
  if (props.slice.properties && props.slice.properties.settings)
    return props.slice.properties.settings

  return {}
})

watch(settings, () => {
  updateSliceStyles()
}, { deep: true })

function openContextMenu(e: MouseEvent) {
  if (isTopLevelWindow()) {
    e.preventDefault()

    adminStore.setCurrentContextItem(props.slice as ElementInterface, e.clientX, e.clientY)
  }
}

function selectCurrentElement(e: MouseEvent) {
  if (isTopLevelWindow()) {
    e.preventDefault()
    pageStore.setCurrentState('currentlySelectedElements', [props.slice])
  }
}

// Pass this down through PageNavigationSlice to PageNavigationField
provide(
  'slice',
  computed(() => {
    return props.slice
  }),
)

// TODO: Is this necessary? It may cause confusion in devtools
provide('fields', fieldsProxy.value)
provide('mountOpen', true)

const { elementInView } = useElementInView(props.slice, sliceRef)
const { highlightStyles, inStudio, onBounding } = useHighlightElement(props.slice, sliceId, sliceStyles)

const boundingOptions = {
  reset: true,
  windowResize: true,
  windowScroll: true,
  immediate: false,
  updateTiming: 'sync',
}

// This figures out if the slice is the topElement
const topElement = inject('topElement', null)
const isFirstElement = ref(false)
const parentIsFirstElement = inject('parentIsFirstElement', false)

if (topElement?.id === props.slice.id) {
  isFirstElement.value = true
}
provide('parentIsFirstElement', isFirstElement.value || parentIsFirstElement)
</script>

<template>
  <template
    v-if="resolvedComponent"
  >
    <!-- {{ fullTailwindConfig.theme.spacing['24'] }}
      <template v-if="sliceRef">
        {{ sliceRef.$el.classList }}
      </template> -->
    <component
      :is="resolvedComponent"
      :id="slice.properties?.dom_id || sliceId"
      :key="slice.properties?.dom_id || sliceId"
      ref="sliceRef"
      v-element-bounding="[onBounding, boundingOptions]"
      :slice="slice"
      :fields="fieldsProxy"
      :in-view="elementInView"
      v-bind="$attrs"
      :class="finalSliceClasses"
      @click.right="openContextMenu"
      @click.meta.exact="selectCurrentElement"
    >
      <template v-for="{ name } in sliceConfig?.slots" #[name]>
        <slot :name="name" />
      </template>
    </component>

    <div
      v-if="inStudio"
      class="fixed z-[9999] box-border transition-opacity duration-200 pointer-events-none"
      :style="highlightStyles"
    />
  </template>
  <DevOnly v-else>
    The component {{ slice.component }} cannot be found. Are you sure you named it correctly?
  </DevOnly>
</template>

<style scoped>
.voix-slice-margin-top {
  margin-top: v-bind("sliceStyles.marginTop");
}

.voix-slice-margin-right {
  margin-right: v-bind("sliceStyles.marginRight");
}

.voix-slice-margin-bottom {
  margin-bottom: v-bind("sliceStyles.marginBottom");
}

.voix-slice-margin-left {
  margin-left: v-bind("sliceStyles.marginLeft");
}

.voix-slice-padding-top {
  padding-top: v-bind("sliceStyles.paddingTop");
}

.voix-slice-padding-right {
  padding-right: v-bind("sliceStyles.paddingRight");
}

.voix-slice-padding-bottom {
  padding-bottom: v-bind("sliceStyles.paddingBottom");
}

.voix-slice-padding-left {
  padding-left: v-bind("sliceStyles.paddingLeft");
}

.voix-slice-color {
  color: v-bind("sliceStyles.color");
}

.voix-slice-background-color {
  background-color: v-bind("sliceStyles.backgroundColor");
}
</style>
